import * as i0 from '@angular/core';
import { Directive, Input, HostBinding, NgModule } from '@angular/core';

const buttonThemes = [
    'primary',
    'accent',
    'secondary',
    'inline',
    'inline-blue',
    'inline-green',
    'inline-red',
    'inline-orange',
    'inline-neutral',
];
const buttonSizes = ['large', 'medium', 'small'];

/**
 * The Angular wrapper for ISPUI Button.
 * **Can only be used with the elements `<button>`, `<input type="button" />`, `<a>` and `<span>`.**
 *
 * Usage:
 * ```html
 * Primary, medium-sized button
 * <button ispuiButton>Apply</button>
 *
 * Inline themed button
 * <button ispuiButton theme="inline-blue">Apply</button>
 *
 * Large button
 * <button ispuiButton size="large">Apply</button>
 *
 * Skeletonized button
 * <button ispuiButton [skeleton]="true">Apply</button>
 *
 * Button-themed link
 * <a ispuiButton href="https://ispsystem.com/about">About</a>
 *
 * Icon button
 * <button ispuiButton [icon]="true">
 *   <svg></svg>
 * </button>
 * ```
 */
class ISPUIButtonDirective {
    constructor(ref) {
        this.ref = ref;
        this.blockClassName = 'ispui-button';
        this.icon = false;
    }
    get element() {
        return this.ref.nativeElement;
    }
    ngOnInit() {
        this.element.classList.add('ispui-button');
        if (this.element instanceof HTMLLinkElement) {
            this.element.setAttribute('role', 'button');
        }
        if (this.element instanceof HTMLSpanElement) {
            this.element.setAttribute('role', 'button');
            this.element.tabIndex = 0;
        }
        if (this.disabled) {
            this.disable();
        }
        if (this.skeleton) {
            this.skeletonize();
        }
        if (this.size) {
            this.setClassName('size', this.size);
        }
        if (this.theme) {
            this.setTheme(this.theme);
        }
    }
    ngOnChanges(change) {
        if (change.theme?.currentValue !== change.theme?.previousValue) {
            this.setTheme(change.theme?.currentValue);
        }
        if (change.size?.currentValue !== change.size?.previousValue) {
            if (!buttonSizes.includes(change.size?.currentValue)) {
                throw new TypeError('The set size is not supported!');
            }
            this.removeClassName('size', change.size?.previousValue);
            this.setClassName('size', change.size?.currentValue);
        }
        if (change.disabled?.currentValue !== change.disabled?.previousValue) {
            if (change.disabled?.currentValue) {
                this.disable();
            }
            else {
                this.enable();
            }
        }
        if (change.skeleton?.currentValue !== change.skeleton?.previousValue) {
            if (change.skeleton?.currentValue) {
                this.skeletonize();
            }
            else {
                this.unskeletonize();
            }
        }
    }
    setTheme(theme) {
        if (!buttonThemes.includes(theme)) {
            throw new TypeError(`The set theme is not supported.`);
        }
        this.unsetTheme();
        this.setClassName('theme', theme === 'inline' ? 'inline-blue' : theme);
    }
    unsetTheme() {
        const themeClass = Array.from(this.element.classList).find(className => className.includes(`_theme_`));
        if (themeClass) {
            this.element.classList.remove(themeClass);
        }
    }
    setClassName(modName, modValue) {
        this.element.classList.add(this.getModClassName(modName, modValue));
    }
    removeClassName(modName, modValue) {
        this.element.classList.remove(this.getModClassName(modName, modValue));
    }
    getModClassName(modName, modValue) {
        let className = `${this.blockClassName}`;
        className += `_${modName}`;
        if (modValue) {
            className += `_${modValue}`;
        }
        return className;
    }
    disable() {
        if (this.element instanceof HTMLButtonElement || this.element instanceof HTMLInputElement) {
            this.element.disabled = true;
        }
        else {
            this.setClassName('disabled');
            this.element.ariaDisabled = 'true';
        }
        if (this.element instanceof HTMLLinkElement) {
            this.hrefCache = `${this.element.href}`;
            this.element.removeAttribute('href');
        }
    }
    enable() {
        if (this.element instanceof HTMLButtonElement || this.element instanceof HTMLInputElement) {
            this.element.disabled = false;
        }
        else {
            this.removeClassName('disabled');
            this.element.ariaDisabled = 'false';
        }
        if (this.element instanceof HTMLLinkElement) {
            this.element.setAttribute('href', `${this.hrefCache}`);
            this.hrefCache = undefined;
        }
    }
    skeletonize() {
        this.setClassName('skeleton');
        this.disable();
    }
    unskeletonize() {
        this.removeClassName('skeleton');
        this.enable();
    }
}
ISPUIButtonDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ISPUIButtonDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: ISPUIButtonDirective, selector: "button[ispuiButton],input[type=button][ispuiButton],a[ispuiButton],span[ispuiButton]", inputs: { theme: "theme", size: "size", skeleton: "skeleton", disabled: "disabled", icon: "icon" }, host: { properties: { "class.ispui-button_icon-only": "this.icon" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'button[ispuiButton],input[type=button][ispuiButton],a[ispuiButton],span[ispuiButton]' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { theme: [{
                type: Input
            }], size: [{
                type: Input
            }], skeleton: [{
                type: Input
            }], disabled: [{
                type: Input
            }], icon: [{
                type: HostBinding,
                args: ['class.ispui-button_icon-only']
            }, {
                type: Input
            }] } });

class ISPUIButtonModule {
}
ISPUIButtonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ISPUIButtonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonModule, declarations: [ISPUIButtonDirective], exports: [ISPUIButtonDirective] });
ISPUIButtonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: ISPUIButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [ISPUIButtonDirective],
                    exports: [ISPUIButtonDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ISPUIButtonDirective, ISPUIButtonModule, buttonSizes, buttonThemes };
